import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';


import { auth } from 'lib/api/firebase';
import { AuthContext, useReduxAuthStore } from 'lib/hooks/auth/use-auth';

import { restoreAuth } from 'features/auth/authSlice';
import { RouteSwitch } from 'navigation/router/route-switch';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { ErrorBoundary } from 'react-error-boundary';

import dayjs from 'dayjs';

/**
 * This code is required in order for dayjs to work with AntD DatePicker
 *
 * This is bad code.
 *
 * TODO: Migrate away from AntD
 */
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { AppLayout } from './app-layout';
import { IS_DEVELOPMENT } from './config';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

declare global {
  // eslint-disable-next-line
  namespace JSX {
    interface IntrinsicElements {
      'stripe-pricing-table': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          'pricing-table-id'?: string;
          'publishable-key'?: string;
          'customer-session-client-secret'?: string;
        },
        HTMLElement
      >;
    }
  }
}


export default function App() {
  const dispatch = useDispatch();

  const authState = useReduxAuthStore();

  useEffect(() => {
    dispatch(restoreAuth(auth));
  }, [dispatch]);

  /**
   * Cache the instance. If we don't do this then the cache will be cleared anytime App.tsx is rerenderd
   * E.g: Route navigation
   */
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchInterval: false,
            refetchIntervalInBackground: false,
            refetchOnMount: true
          }
        }
      }),
    []
  );
  return (
    <AuthContext value={authState}>
      <AppLayout>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary
            fallbackRender={props => (
              <ErrorDisplay error={props.error} description="" title="" />
            )}
          >
            <RouteSwitch />
          </ErrorBoundary>
          {IS_DEVELOPMENT && (
            <TanStackDevtools
              plugins={[
                {
                  name: 'TanStack Query',
                  render: (
                    <ReactQueryDevtoolsPanel
                      // WARNING: We set `setIsOpen` manually because the v4 version of the library requires it. This does not happen in the v5 version.
                      setIsOpen={() => !!0}
                      // WARNING: We set `onDragStart` manually because the v4 version of the library requires it. This does not happen in the v5 version.
                      onDragStart={() => !!0}
                    />
                  )
                }
              ]}
            />
          )}
        </QueryClientProvider>
      </AppLayout>
    </AuthContext>
  );
}
